import { ClipboardCopy } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@buildpy/ui/button";

interface Props {
  // 서버가 만든 네이버용 서식(url 모드) — 막힌 사례면 null.
  html: string | null;
  text: string | null;
  blockedReason: string | null;
  // 공개 주소가 없어 뺀 사진 수.
  omitted: number;
  // 개발 주소(http) 경고 — 복사는 막지 않는다.
  warning: string | null;
}

// 서식(text/html)과 평문(text/plain)을 같이 — ClipboardItem 이 없거나 거절되면 copy 이벤트로 한 번 더.
const writeRich = async (html: string, text: string): Promise<void> => {
  if (typeof ClipboardItem !== "undefined" && navigator.clipboard?.write) {
    try {
      await navigator.clipboard.write([
        new ClipboardItem({
          "text/html": new Blob([html], { type: "text/html" }),
          "text/plain": new Blob([text], { type: "text/plain" }),
        }),
      ]);
      return;
    } catch {
      // 권한·포커스 문제면 아래 대체 경로로.
    }
  }
  const onCopy = (event: ClipboardEvent) => {
    event.clipboardData?.setData("text/html", html);
    event.clipboardData?.setData("text/plain", text);
    event.preventDefault();
  };
  document.addEventListener("copy", onCopy);
  try {
    if (!document.execCommand("copy")) throw new Error("copy rejected");
  } finally {
    document.removeEventListener("copy", onCopy);
  }
};

// 시공사례 「네이버용 복사」 — 공개 사진 주소를 건 서식이라 공개 사례에서만 누를 수 있다.
const NaverCopyButton = ({ html, text, blockedReason, omitted, warning }: Props) => {
  const copy = async () => {
    if (html === null || text === null) return;
    try {
      await writeRich(html, text);
      const notes = [...(warning ? [warning] : []), ...(omitted > 0 ? [`사진 ${omitted}장은 공개 주소가 없어 뺐습니다`] : [])];
      toast.success("네이버 블로그 글쓰기 화면에 붙여 넣으세요", {
        description: notes.length > 0 ? notes.join(" · ") : undefined,
      });
    } catch {
      toast.error("복사하지 못했습니다 — 사진 묶음(ZIP)의 글.html 을 열어 복사해 주세요");
    }
  };

  const blocked = blockedReason !== null || html === null;
  return (
    <div className="flex min-w-0 flex-col gap-y-1" data-testid="portfolio-naver-copy">
      <Button disabled={blocked} size="sm" type="button" variant="outline" onClick={copy}>
        <ClipboardCopy className="size-4" />
        네이버용 복사
      </Button>
      {blockedReason && (
        <p className="text-xs text-muted-foreground" data-testid="portfolio-naver-copy-blocked">
          {blockedReason}
        </p>
      )}
    </div>
  );
};

export default NaverCopyButton;
